import { SESSION_STORAGE_KEYS, LOCAL_STORAGE_KEYS } from './constants';
import { getLinkToLogin } from './getLinkToLogin';
import { EKioskRouterNames, ELanguage, TNullable } from '@/types';

type TNavigateToLoginProps = {
  subdomain?: TNullable<string>;
  institutionId?: string;
  attachToken?: boolean;
  email?: string;
  language?: ELanguage;
};

/**
 * Navigates the user to the login page, optionally attaching query parameters and access token.
 * @param props - Navigation options.
 * @param props.subdomain - Optional subdomain to use for the login URL; defaults to environment-specific subdomain.
 * @param props.institutionId - Optional institution ID to attach as a query parameter.
 * @param props.attachToken - If true, attaches the stored access token as a URL hash.
 * @param props.email - Optional email to attach as a query parameter.
 * @param props.language - Optional language to attach as a query parameter.
 */
export const navigateToLogin = ({
  subdomain,
  institutionId,
  attachToken,
  email,
  language,
}: TNavigateToLoginProps) => {
  const baseSubdomain = subdomain ?? import.meta.env.VITE_DEFAULT_SUBDOMAIN;
  const baseUrl = import.meta.env.VITE_MY_URL.replace(
    'subdomain',
    baseSubdomain,
  );

  const url = new URL(getLinkToLogin(), baseUrl);

  if (institutionId) {
    url.searchParams.set('institutionId', institutionId);
  }

  if (language) {
    url.searchParams.set('language', language);
  }

  if (email) {
    url.searchParams.set('email', email);
  }

  const redirect = sessionStorage.getItem(SESSION_STORAGE_KEYS.REDIRECT_PATH);
  if (redirect) {
    url.searchParams.set(SESSION_STORAGE_KEYS.REDIRECT_PATH, redirect);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_PATH);
  }

  if (attachToken) {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      url.hash = token;
    }
  }

  window.location.replace(url.toString());
};

type TNavigateToActivationKioskProps = {
  accessKioskToken: boolean;
  kioskId: string;
  subdomain?: TNullable<string>;
  language?: ELanguage;
};

/**
 * Navigates the user to the login page, optionally attaching query parameters and access token.
 * @param props - Navigation options.
 * @param props.subdomain - Optional subdomain to use for the login URL; defaults to environment-specific subdomain.
 * @param props.kioskId - kiosk ID to attach as a query parameter.
 * @param props.accessKioskToken - If true, attaches the stored access token as a URL hash.
 * @param props.language - Optional language to attach as a query parameter.
 */
export const navigateToActivationKiosk = ({
  subdomain,
  accessKioskToken,
  language,
  kioskId,
}: TNavigateToActivationKioskProps) => {
  const baseSubdomain = subdomain ?? import.meta.env.VITE_DEFAULT_SUBDOMAIN;
  const baseUrl = import.meta.env.VITE_MY_URL.replace(
    'subdomain',
    baseSubdomain,
  );

  const url = new URL(EKioskRouterNames.KIOSK_ACTIVATION, baseUrl);

  if (language) {
    url.searchParams.set('language', language);
  }

  if (kioskId) {
    url.searchParams.set('kioskId', kioskId);
  }

  const redirect = sessionStorage.getItem(SESSION_STORAGE_KEYS.REDIRECT_PATH);
  if (redirect) {
    url.searchParams.set(SESSION_STORAGE_KEYS.REDIRECT_PATH, redirect);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_PATH);
  }

  if (accessKioskToken) {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.KIOSK_TOKEN);
    if (token) {
      url.hash = token;
    }
  }

  window.location.replace(url.toString());
};
